import * as React from 'react';
import * as _ from 'lodash';
import { connect, Dispatch } from 'react-redux';
import { IAccountsReceivable, IBaseResponse, ISelectedCenterOrgan } from '@nkr/models/index';
import { IApplicationState, AppActionCreators, ReportActionCreators, IAccountsReceivableReportResponse } from '@nkr/store/index';
import { ColumnWidthCallbackParams, ExcelExportParams, FirstDataRenderedEvent, GridApi, GridReadyEvent, ProcessCellForExportParams, RowDataChangedEvent } from 'ag-grid-community';
import { Grid as BsContainer, Button, Col, FormControl, Row, Well, Modal, ToggleButton, ToggleButtonGroup } from 'react-bootstrap';
import ArApNav from '../ar-ap-nav';
import { RouteComponentProps } from 'react-router';
import Grid from './grid';
import { toast } from 'react-toastify';
import { OrganType } from '@nkr/enums/organ-type';
import { downloadFile } from '@nkr/services/download-file';
import { AsOfExportModal } from '../as-of-export-modal';

interface IOwnProps extends RouteComponentProps { }

interface IStateProps { }

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getAccountsReceivable: (organType: OrganType | null) => Promise<IAccountsReceivableReportResponse>,
    sendDelinquentEmail: (selections: ISelectedCenterOrgan[], emailText: string) => Promise<IBaseResponse>,
    getAsOfReport: (date: string) => Promise<any>,
}

type TProps = IStateProps & IDispatchProps & IOwnProps

interface IState {
    data: IAccountsReceivable[],
    centerId: number,
    organTypes: OrganType[],
    gridApi: GridApi,
    showEmailModal?: boolean,
    selectedCenters?: ISelectedCenterOrgan[],
    emailText?: string,
    showAsOfModal: boolean,
}

class AccountsReceivable extends React.Component<TProps, IState> {
    readonly state: IState = {
        data: [],
        centerId: 0,
        organTypes: [OrganType.Kidney, OrganType.Liver],
        gridApi: null,
        showEmailModal: false,
        selectedCenters: [],
        showAsOfModal: false,
    }

    onGridReady = (e: GridReadyEvent) => {
        this.setState({
            gridApi: e.api
        });

        e.api.sizeColumnsToFit();
    }

    showModal = (show: boolean) => {
        this.setState({
            showEmailModal: show,
            emailText: "This is a follow-up regarding the delinquent invoice notice last sent on {LAST_NOTICE}. As of today, your payment has not been received.\r\n\r\nPlease respond to this email with an update when payment can be expected."
        });
    }

    setSelectedCenters = (selections: ISelectedCenterOrgan[]) => {
        this.setState({ selectedCenters: selections });
    }

    handleEmailTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        this.setState({
            emailText: e.target.value
        });
    }

    sendEmails = () => {
        this.props.sendDelinquentEmail(this.state.selectedCenters, this.state.emailText).then((response) => {
            if (response?.data?.success) {
                toast.success(`Emails sent to ${this.state.selectedCenters.length} centers successfully.`);
                this.showModal(false);
                this.getData();
            }
        });
    }

    changeOrganType = (values: OrganType | OrganType[]) => {
        let safeValues = Array.isArray(values) ? values : [values];
        if (safeValues.length == 0) return;
        this.setState({ organTypes: safeValues }, () => this.getData());
    }

    onFirstDataRendered = (e: FirstDataRenderedEvent) => {
        e.api.sizeColumnsToFit();
    }

    onRowDataChanged = (e: RowDataChangedEvent) => {
        e.api.sizeColumnsToFit();
    }

    componentDidMount = () => {
        this.getData();
    }

    getData = () => {
        let { organTypes } = this.state;
        let organType = organTypes.length == 2 ? null : organTypes[0];

        this.props.showLoading('Getting report data, one moment please...').then(() => {
            this.props.getAccountsReceivable(organType).then((response) => {
                if (response.data.success) {
                    let list = response.data.results;

                    this.setState({
                        data: list,
                    }, () => {
                        this.props.hideLoading();
                    });
                } else {
                    this.props.hideLoading();
                }
            });
        });
    };

    getFilteredData = () => {
        return _.cloneDeep(this.state.data).filter(x => {
            let centerFilter = this.state.centerId > 0 ? x.centerId == this.state.centerId : true;

            return centerFilter;
        });
    }

    handleCenterChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        this.setState({
            centerId: parseInt(e.currentTarget.value)
        });
    }

    handleExport = () => {
        let params: ExcelExportParams = {
            columnGroups: true,
            fileName: 'accounts-receivable-aging.xlsx',
            columnWidth: (params: ColumnWidthCallbackParams) => {
                let colDef = params.column.getColDef();
                return colDef.width;
            },
            processCellCallback: (params: ProcessCellForExportParams) => {
                let colDef = params.column.getColDef();

                if (colDef.field == 'organType')
                    return params.value == 0 ? 'Kidney' : params.value == 1 ? 'Liver' : params.value;

                if (colDef.colId == 'centerAlias') {
                    if (params.node.data.centerAlias == '') {
                        return 'Totals';
                    }
                }

                if (colDef.colId == 'centerPaysViaAch') {
                    if (params.node.data.centerAlias != '') {
                        return params.value ? 'Y' : 'N';
                    } else {
                        return '';
                    }
                }

                if (colDef.colId == 'rank') {
                    if (params.node.data.centerAlias != '') {
                        return params.value;
                    } else {
                        return '';
                    }
                }

                if (colDef.colId == 'netRisk') {
                    return params.node.data.totalDelinquent - params.node.data.ccbApTotal;
                }

                return params.value;
            },
        };

        this.state.gridApi.exportDataAsExcel(params);
    }

    handleExportAsOf = (date: string) => {
        this.setState({ showAsOfModal: false });

        this.props.showLoading('Generating report, one moment please...').then(() => {
            this.props.getAsOfReport(date)
                .then((response) => {
                    downloadFile(response.data, `accounts-receivable-as-of_${date}.csv`, 'text/csv');
                })
                .finally(() => this.props.hideLoading());
        });
    }

    render = () => {
        const centerList = _.sortBy(_.uniqBy(this.state.data.map(x => ({ id: x.centerId, alias: x.centerAlias })), item => item.id), item => item.alias);
        const filteredData = this.getFilteredData();
        const organValue = this.state.organTypes.length == 2 ? -1 : this.state.organTypes[0];

        return (
            <BsContainer fluid>

                <ArApNav href={this.props.location.pathname} />

                <Well bsSize='sm'>
                    <Row>
                        <Col xs={12} sm={6}>
                            <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }}>
                                <div style={{ marginRight: 5, whiteSpace: 'nowrap' }}>Center:</div>
                                <div style={{ marginRight: 25 }}>
                                    <FormControl
                                        componentClass='select'
                                        value={this.state.centerId}
                                        onChange={this.handleCenterChange}
                                    >
                                        <option value={0}>- All Centers -</option>
                                        {centerList.map(center =>
                                            <option key={`ctr-${center.id}`} value={center.id}>{center.alias}</option>
                                        )}
                                    </FormControl>
                                </div>
                                <div style={{ marginRight: 5, whiteSpace: 'nowrap' }}>Organ:</div>
                                <div className="btn-group" data-toggle="buttons">
                                    <ToggleButtonGroup type='radio' name='organs' value={organValue}>
                                        <ToggleButton value={-1} onChange={() => this.changeOrganType([OrganType.Kidney, OrganType.Liver])}
                                            className={this.state.organTypes.length == 2 ? 'btn-primary btn-md' : 'btn-md'}>All</ToggleButton>
                                        <ToggleButton value={OrganType.Kidney} onChange={() => this.changeOrganType(OrganType.Kidney)}
                                            className={this.state.organTypes.length == 1 && this.state.organTypes.includes(OrganType.Kidney) ? 'btn-primary btn-md' : 'btn-md'}>Kidney</ToggleButton>
                                        <ToggleButton value={OrganType.Liver} onChange={() => this.changeOrganType(OrganType.Liver)}
                                            className={this.state.organTypes.length == 1 && this.state.organTypes.includes(OrganType.Liver) ? 'btn-primary btn-md' : 'btn-md'}>Liver</ToggleButton>
                                    </ToggleButtonGroup>
                                </div>
                            </div>
                        </Col>
                        <Col xs={12} sm={2}>
                            <div style={{ lineHeight: '30px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                <div className='bg-risk' style={{ width: 12, height: 12, display: 'inline-block', marginRight: 5 }}></div>
                                    Net Risk &gt; 0
                                </div>
                        </Col>
                        <Col xs={12} sm={4}>
                            <div style={{ float: 'right', whiteSpace: 'nowrap' }}>
                                <Button bsStyle='success' onClick={() => this.showModal(true)}>Email Checked</Button>
                                <Button onClick={this.handleExport} style={{ marginLeft: 8 }}>Export to Excel</Button>
                                <Button onClick={() => this.setState({ showAsOfModal: true })} style={{ marginLeft: 8 }}>Export As-Of</Button>
                            </div>
                        </Col>
                    </Row>
                </Well>

                <Grid
                    data={filteredData}
                    onGridReady={this.onGridReady}
                    onFirstDataRendered={this.onFirstDataRendered}
                    onRowDataChanged={this.onRowDataChanged}
                    setSelectedCenters={this.setSelectedCenters} />

                <div>* <strong>[Net Risk]</strong> = <strong>[Total Delinquent]</strong> minus <strong>[NKR CCB AP 151+ Days]</strong> for auditing</div>

                <Modal show={this.state.showEmailModal} onHide={() => this.showModal(false)} bsSize='lg'>
                    <Modal.Header closeButton>
                        <Modal.Title>Email Accounts Receivable Report</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>You have selected {this.state.selectedCenters.length} centers to send the e-mail to. Please ensure the text below is
                            what you want to send as the introductory message in the e-mail.</p>

                        <textarea rows={8} style={{ width: '100%' }} value={this.state.emailText} onChange={this.handleEmailTextChange} />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button onClick={() => this.showModal(false)}>Cancel</Button>
                        <Button bsStyle='primary' disabled={this.state.selectedCenters.length === 0} onClick={this.sendEmails}>Email Report</Button>
                    </Modal.Footer>
                </Modal>

                <AsOfExportModal
                    show={this.state.showAsOfModal}
                    onClose={() => this.setState({ showAsOfModal: false })}
                    onExport={this.handleExportAsOf} />
            </BsContainer>
        );
    }
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {}
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getAccountsReceivable: (organType: OrganType | null) => {
            return dispatch(ReportActionCreators.getAccountsReceivable(organType));
        },
        sendDelinquentEmail: (selections: ISelectedCenterOrgan[], emailText: string) => {
            return dispatch(ReportActionCreators.sendDelinquentEmail(selections, emailText));
        },
        getAsOfReport: (date: string) => {
            return dispatch(ReportActionCreators.getBillingAsOfReport('accounts-receivable', date));
        }
    }
}

export default connect<IStateProps, IDispatchProps, IOwnProps>(
    mapStateToProps,
    mapDispatchToProps)(AccountsReceivable);